import React, { useEffect, useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { applyDocumentDirection } from "../helper/documentDirection";
import {
  CONTROL_HEIGHT,
  CONTROL_RADIUS,
  NJIM,
} from "./njimTheme";

type AppThemeProviderProps = {
  children: React.ReactNode;
};

const FONT_STACK = '"Arimo", "Noto Sans Arabic", sans-serif';

/**
 * App-wide MUI theme synced with i18n direction.
 * Ensures Button icon spacing and adornment gaps work in Arabic (RTL).
 */
const AppThemeProvider: React.FC<AppThemeProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");

  useEffect(() => {
    applyDocumentDirection(i18n.language);
  }, [i18n.language]);

  const theme = useMemo(
    () =>
      createTheme({
        direction: isRTL ? "rtl" : "ltr",
        palette: {
          primary: {
            main: NJIM.ink,
            contrastText: NJIM.white,
          },
          secondary: {
            main: NJIM.mint,
            contrastText: NJIM.mintForeground,
          },
          success: { main: NJIM.success },
          warning: { main: NJIM.warning },
          error: { main: NJIM.danger },
          info: { main: NJIM.info },
          text: {
            primary: NJIM.textPrimary,
            secondary: NJIM.textSecondary,
            disabled: NJIM.textMuted,
          },
          divider: NJIM.border,
          background: {
            default: NJIM.muted,
            paper: NJIM.white,
          },
        },
        shape: {
          borderRadius: 12,
        },
        typography: {
          fontFamily: FONT_STACK,
          h1: { fontSize: 24, fontWeight: 700, color: NJIM.ink },
          h2: { fontSize: 22, fontWeight: 700, color: NJIM.ink },
          h3: { fontSize: 18, fontWeight: 600, color: NJIM.ink },
          h4: { fontSize: 16, fontWeight: 600, color: NJIM.textPrimary },
          body1: { fontSize: 14, fontWeight: 400, color: NJIM.textPrimary },
          body2: { fontSize: 13, fontWeight: 400, color: NJIM.textSecondary },
          caption: { fontSize: 12, fontWeight: 500, color: NJIM.textSecondary },
          button: { fontWeight: 600, textTransform: "none" },
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              html: { fontFamily: FONT_STACK },
              body: { fontFamily: FONT_STACK },
              "@media (prefers-reduced-motion: reduce)": {
                "*, *::before, *::after": {
                  animationDuration: "0.01ms !important",
                  animationIterationCount: "1 !important",
                  transitionDuration: "0.01ms !important",
                },
              },
            },
          },
          MuiButton: {
            defaultProps: {
              disableRipple: false,
            },
            styleOverrides: {
              root: {
                gap: 8,
                fontFamily: FONT_STACK,
                textTransform: "none",
                borderRadius: 999,
                minHeight: CONTROL_HEIGHT,
                transition:
                  "transform 150ms ease-out, background-color 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                "&:active:not(:disabled)": {
                  transform: "scale(0.97)",
                },
                "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                  margin: 0,
                  flexShrink: 0,
                },
                "& .MuiButton-startIcon > *:nth-of-type(1), & .MuiButton-endIcon > *:nth-of-type(1)":
                  {
                    fontSize: 20,
                  },
              },
              startIcon: { margin: 0 },
              endIcon: { margin: 0 },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                minWidth: 40,
                minHeight: 40,
                "&:active": { transform: "scale(0.96)" },
                transition: "transform 150ms ease-out, background-color 180ms ease",
                "&:focus-visible": {
                  outline: `2px solid ${NJIM.mint}`,
                  outlineOffset: 2,
                },
              },
            },
          },
          MuiTextField: {
            defaultProps: {
              size: "medium",
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                minHeight: CONTROL_HEIGHT,
                borderRadius: CONTROL_RADIUS,
                backgroundColor: NJIM.white,
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: NJIM.mint,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: NJIM.mint,
                  borderWidth: 2,
                },
              },
              notchedOutline: {
                borderColor: NJIM.border,
              },
              input: {
                fontSize: 14,
                color: NJIM.textPrimary,
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              select: {
                minHeight: CONTROL_HEIGHT,
                display: "flex",
                alignItems: "center",
                boxSizing: "border-box",
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              head: {
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.3px",
                textTransform: "uppercase",
                color: NJIM.textSecondary,
                backgroundColor: NJIM.muted,
                borderBottom: `1px solid ${NJIM.border}`,
              },
              body: {
                fontSize: 14,
                color: NJIM.textPrimary,
                borderBottom: `1px solid ${NJIM.border}`,
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                fontWeight: 600,
                fontSize: 12,
                borderRadius: 999,
                height: 26,
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                borderRadius: 16,
                border: `1px solid ${NJIM.border}`,
                boxShadow: NJIM.elevationLg,
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 600,
                minHeight: 40,
                color: NJIM.textSecondary,
                "&.Mui-selected": {
                  color: NJIM.ink,
                },
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                backgroundColor: NJIM.white,
                color: NJIM.textPrimary,
                fontSize: 13,
                fontWeight: 400,
                boxShadow: NJIM.elevationLg,
                border: `1px solid ${NJIM.border}`,
                borderRadius: 8,
              },
              arrow: {
                color: NJIM.white,
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
              elevation1: {
                boxShadow: NJIM.elevation,
              },
            },
          },
          MuiInputAdornment: {
            styleOverrides: {
              root: {
                "&.MuiInputAdornment-positionStart": {
                  marginRight: 0,
                  marginInlineEnd: "8px",
                },
                "&.MuiInputAdornment-positionEnd": {
                  marginLeft: 0,
                  marginInlineStart: "8px",
                },
              },
            },
          },
          MuiInputBase: {
            styleOverrides: {
              root: {
                fontFamily: FONT_STACK,
              },
              input: {
                textAlign: "start",
                "&[type='password']::-ms-reveal": { display: "none" },
                "&[type='password']::-ms-clear": { display: "none" },
              },
              inputAdornedEnd: isRTL
                ? { paddingLeft: 0, paddingRight: 14 }
                : { paddingRight: 0 },
              inputAdornedStart: isRTL
                ? { paddingRight: 0, paddingLeft: 14 }
                : { paddingLeft: 0 },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              outlined: isRTL
                ? {
                    left: "auto",
                    right: 0,
                    transformOrigin: "top right",
                    transform: "translate(-14px, 16px) scale(1)",
                    "&.MuiInputLabel-sizeSmall": {
                      transform: "translate(-14px, 9px) scale(1)",
                    },
                    "&.MuiInputLabel-shrink": {
                      transform: "translate(-14px, -9px) scale(0.75)",
                    },
                    "&.MuiInputLabel-sizeSmall.MuiInputLabel-shrink": {
                      transform: "translate(-14px, -9px) scale(0.75)",
                    },
                  }
                : {},
            },
          },
        },
      }),
    [isRTL]
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default AppThemeProvider;
